import { type Hex, distance } from '../core/hex';
import type { Tile, TileType } from '../core/tile';

/**
 * Result of a `Movement.tryMove` call. Discriminated by `ok`.
 *
 * Block reasons (slice 1):
 *   - `non-neighbour`: target is not exactly one hex away from current position
 *   - `impassable`: target tile blocks entry (e.g. `mountain`) or has not been
 *     generated yet (caller responsibility to ensure tiles exist before letting
 *     the player click them)
 */
export type MoveResult =
  | { ok: true; position: Hex }
  | { ok: false; reason: 'non-neighbour' | 'impassable' };

export interface TileSource {
  getTileAt(hex: Hex): Tile | null | undefined;
}

/**
 * Slice-1 passability rule per ADR-0004: mountains block traversal; everything
 * else is passable. Dangerous tiles are passable in slice 1.
 *
 * TODO(slice-2): apply damage on enter for `dangerous`.
 */
export function isPassable(type: TileType): boolean {
  return type !== 'mountain';
}

/**
 * Movement owns the active player position (per the ticket — `Character.position`
 * is reconciled at retire / load by HEX-12). Tile lookups are injected so the
 * module stays decoupled from any concrete world store.
 */
export class Movement {
  private currentPosition: Hex;
  private readonly tiles: TileSource;

  constructor(initialPosition: Hex, tiles: TileSource) {
    this.currentPosition = initialPosition;
    this.tiles = tiles;
  }

  get position(): Hex {
    return this.currentPosition;
  }

  /**
   * Relocate the active position to an arbitrary hex, bypassing the adjacency
   * rule (HEX-92 teleport-pillar fast travel; ADR-0004 travel primitive).
   * A teleport is a deliberate discontinuity — the pillar flow has already vetted
   * the destination (a known pillar) and charged for it — so this skips the
   * neighbour + passability checks `tryMove` enforces for walking.
   */
  teleportTo(target: Hex): void {
    this.currentPosition = target;
  }

  tryMove(target: Hex): MoveResult {
    if (distance(this.currentPosition, target) !== 1) {
      return { ok: false, reason: 'non-neighbour' };
    }
    const tile = this.tiles.getTileAt(target);
    if (!tile || !isPassable(tile.type)) {
      return { ok: false, reason: 'impassable' };
    }
    this.currentPosition = target;
    return { ok: true, position: target };
  }
}
